// Who's online + how long people worked, on one page, system owner only.
// Refreshes itself every 15 s while open; nothing is pushed to anyone else
// (no green dot, by choice). Rules and the caution: presence.md.
import { useEffect, useState } from "react";
import { Alert, Badge, Form, Table } from "react-bootstrap";
import RulesHelp from "../shared/RulesHelp.jsx";
import { formatDate, todayInput } from "../../utils/date.js";
import { getPresenceNow, getPresenceDay, errorMessage, isAbortError } from "../../services/apiClient.js";

const STATUS = { online: "success", idle: "warning", away: "secondary" };
const time = (v) => (v ? new Date(v).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" }) : "—");
const ago = (v) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(v)) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m ago`;
};
const hours = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

function WhosOnlinePanel() {
  const [now, setNow] = useState(null);
  const [day, setDay] = useState(todayInput());
  const [dayData, setDayData] = useState(null);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    Promise.all([getPresenceNow({ signal: ac.signal }), getPresenceDay(day, { signal: ac.signal })])
      .then(([n, d]) => { setNow(n); setDayData(d); setError(""); })
      .catch((e) => { if (!isAbortError(e)) setError(errorMessage(e, "Unable to load who's online.")); });
    return () => ac.abort();
  }, [day, tick]);

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <h5 className="mb-0">Who's online</h5>
        <RulesHelp topic="whosOnline" />
        <span className="text-muted small ms-auto">refreshes every 15 s while open</span>
      </div>
      {error ? <Alert variant="danger" className="py-1 small">{error}</Alert> : null}

      <Table size="sm" className="align-middle">
        <thead><tr><th style={{ width: "26%" }}>Who</th><th style={{ width: "10%" }}>Status</th><th style={{ width: "16%" }}>Last seen</th><th>On</th><th className="text-end" style={{ width: "12%" }}>Requests today</th></tr></thead>
        <tbody>
          {now?.users.length ? now.users.map((u) => (
            <tr key={u.id}>
              <td>{u.full_name} <span className="text-muted small">{u.username}</span></td>
              <td><Badge bg={STATUS[u.status]} text={u.status === "idle" ? "dark" : undefined}>{u.status}</Badge></td>
              <td data-hint={time(u.last_seen)}>{ago(u.last_seen)}</td>
              <td className="text-muted small">{u.page || "—"}</td>
              <td className="text-end">{u.requests_today}</td>
            </tr>
          )) : (
            <tr><td colSpan={5} className="text-muted text-center">Nobody has been seen today{now ? " (since the server last started)" : ""}.</td></tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex align-items-center gap-3 mt-4 mb-2">
        <h5 className="mb-0">Who worked, and how long</h5>
        <Form.Control type="date" size="sm" style={{ width: "11rem" }} value={day} max={todayInput()} onChange={(e) => setDay(e.target.value || todayInput())} />
        <span className="text-muted small">{day === todayInput() ? "today" : formatDate(day)}</span>
      </div>
      <Table size="sm" className="align-middle">
        <thead><tr><th style={{ width: "26%" }}>Who</th><th>First seen</th><th>Last seen</th><th>Active</th><th className="text-end">Requests</th></tr></thead>
        <tbody>
          {dayData?.users.length ? dayData.users.map((u) => (
            <tr key={u.id}>
              <td>{u.full_name} <span className="text-muted small">{u.username}</span></td>
              <td>{time(u.first_seen)}</td>
              <td>{time(u.last_seen)}</td>
              <td>{hours(u.active_minutes)}</td>
              <td className="text-end">{u.requests}</td>
            </tr>
          )) : (
            <tr><td colSpan={5} className="text-muted text-center">No activity recorded for this day.</td></tr>
          )}
        </tbody>
      </Table>
      <div className="text-muted small">
        Active = five-minute slots with at least one request. Reading a quote or typing a long note for
        twenty minutes shows as one or two slots, so this shows who was here, not a timesheet.
        {dayData?.days?.length ? <> Days with activity: {dayData.days.slice(0, 10).map((d) => formatDate(d)).join(", ")}{dayData.days.length > 10 ? "…" : ""}.</> : null}
      </div>
    </div>
  );
}

export default WhosOnlinePanel;
